import { scoringVersion } from "./version.js";
/**
 * Volet 2 — taux d'automatisation par tâche (docs/SCORING.md §6).
 * L'ordre est celui de la maquette, qui fait foi pour l'écran existant.
 * Ces taux sont les nôtres — contrairement aux bases d'exposition du volet 1,
 * ils ne sont dérivés d'aucune source publiée. Ils sont documentés publiquement
 * au même titre que le reste du barème.
 */
export const TIME_COST_TASKS = [
    { name: "Saisie et recopie de données", auto: 0.8 },
    /*
      « courriels » et non « mails » — 09/09/2026.
  
      Le produit dit « courriel » partout ailleurs : son module d'envoi porte ce
      nom, et la page légale l'emploie. Ce libellé, lu sur l'écran du volet 2 et
      republié dans la méthode, disait « mails ». Trois mots pour la même chose
      sur une même surface, dont un anglicisme là où le reste tient un français
      soigné.
  
      Le libellé voisin garde « reporting », et c'est délibéré : le mot est
      naturalisé dans le français professionnel de la zone, la grille de qualité
      ne bannit d'ailleurs que « reporting tool ». Le remplacer rendrait
      l'étiquette moins reconnaissable à celui qui la coche. La cohérence est
      l'argument, pas le purisme.
  
      Les libellés ne servent qu'à l'affichage : le calcul suit l'ordre de cette
      liste, jamais les noms. Aucun indice ne bouge.
    */
    { name: "Rédaction de courriers, courriels et notes", auto: 0.7 },
    { name: "Tableaux, reporting et mise en forme", auto: 0.65 },
    { name: "Relances et suivis répétitifs", auto: 0.75 },
    { name: "Comptes rendus de réunion", auto: 0.85 },
    { name: "Recherche d'informations et vérifications", auto: 0.55 },
];
/** Semaines par mois, pour convertir un temps hebdomadaire en temps mensuel. */
export const WEEKS_PER_MONTH = 4.33;
/** Heures travaillées par mois, pour déduire le taux horaire du salaire net. */
export const HOURS_PER_MONTH = 173;
/**
 * La semaine et la journée de travail — révisées le 03/09/2026.
 *
 * Quarante heures par semaine, cinq journées de huit heures : c'est la semaine
 * légale de la zone, et c'est ce que 173 heures par mois encodaient déjà sans
 * le dire — 40 × 52 ÷ 12 = 173,3. La journée était jusqu'ici *déduite* d'un
 * mois posé à vingt journées, ce qui la faisait durer 8,65 h et contredisait le
 * 5 × 8 que tout le monde connaît. On pose donc la semaine et la journée, et
 * c'est le nombre de journées qui se déduit.
 */
export const HEURES_PAR_SEMAINE = 40;
export const HEURES_PAR_JOUR = 8;
/**
 * Journées travaillées par mois — l'unité dans laquelle un volume se comprend.
 *
 * « 96 heures par mois » demande un calcul mental que personne ne fait ; « 12
 * journées » se voit. Mais une journée ne dit rien sans son mois : douze
 * journées paraissent peu, douze sur vingt-deux sont énormes. Le dénominateur
 * est donc affiché avec le chiffre, partout.
 *
 * Déduit : 173 ÷ 8 = 21,6, affiché 22. Un entier, parce que c'est un
 * dénominateur qu'on lit, et arrondi de la même façon que les journées qu'on
 * lui compare — de sorte qu'un mois entier fasse exactement « 22 sur 22 ».
 */
export const JOURS_TRAVAILLES_PAR_MOIS = Math.round(HOURS_PER_MONTH / HEURES_PAR_JOUR);
/** Un volume d'heures mensuelles, exprimé en journées de travail. */
export function enJournees(heuresParMois) {
    return Math.round(heuresParMois / HEURES_PAR_JOUR);
}
/**
 * Les heures récupérables **par semaine** sur les tâches réelles, avant la
 * conversion mensuelle — 03/09/2026.
 *
 * C'est le chiffre que la couverture du rapport imprime et que la carte de
 * partage reprend. Il vient du barème, pas du modèle : la règle n° 2 dit que le
 * score est calculé par le code, jamais par le modèle, et ce nombre-là est de
 * la même nature. La couverture portait jusqu'ici la somme des gains que le
 * modèle avait écrits sur ses dix actions — 15 h là où le barème en rendait
 * 11,7 sur le même profil. Le modèle reçoit désormais ce plafond, et ses
 * actions doivent tenir dedans.
 *
 * Même formule que `computeTimeCostFromTasks`, sans le × 4,33 : le site dit
 * « par mois », le rapport dit « par semaine », et les deux se recoupent.
 */
export function recuperablesParSemaine(taches) {
    const total = taches.reduce((somme, t) => somme + heuresValides(t.heures) * AUTO_PAR_NIVEAU[t.niveau], 0);
    return Math.round(total * 10) / 10;
}
/** Tranches de rémunération et valeurs médianes utilisées (docs/SCORING.md §6). */
export const PAY_BRACKETS = [
    { valeur: 120000, libelle: "Moins de 150 000 F" },
    { valeur: 220000, libelle: "150 000 à 300 000 F" },
    { valeur: 430000, libelle: "300 000 à 600 000 F" },
    { valeur: 850000, libelle: "600 000 à 1 200 000 F" },
    { valeur: 1500000, libelle: "Plus de 1 200 000 F" },
];
/**
 * Part récupérable selon le niveau d'automatisation de la tâche.
 *
 * Sert à chiffrer les **tâches réelles de l'utilisateur**, celles que le volet 1
 * a produites pour son métier, plutôt que la liste générique ci-dessus. Les
 * valeurs se tiennent dans la même fourchette que `TIME_COST_TASKS` (0,55 à
 * 0,85 pour ce qui s'automatise largement), et `low` n'est pas nul : une tâche
 * qui résiste garde une préparation autour d'elle — un compte rendu de visite,
 * la mise au propre d'un relevé — et c'est cette part-là qui se récupère.
 *
 * Comme les taux de la liste générique, ce sont **nos** valeurs : elles ne
 * dérivent d'aucune source publiée et sont documentées dans docs/SCORING.md §6.
 */
export const AUTO_PAR_NIVEAU = {
    high: 0.75,
    mid: 0.4,
    low: 0.1,
};
/**
 * Calcule le temps récupérable et sa valeur annuelle.
 *
 *   heures_récupérables_mois = Σ ( heures_semaine × taux ) × 4,33
 *   taux_horaire             = rémunération_mensuelle_nette / 173
 *   valeur_annuelle          = heures × taux_horaire × 12, arrondie au millier
 *
 * La valeur annuelle part des heures arrondies : c'est le nombre affiché à
 * l'utilisateur, et le montant doit rester cohérent avec lui.
 */
function chiffrer(rows, remunerationMensuelle) {
    const hours = Math.round(rows.reduce((sum, r) => sum + r.recuperables, 0));
    const hourly = remunerationMensuelle / HOURS_PER_MONTH;
    const money = Math.round((hours * hourly * 12) / 1000) * 1000;
    return { hours, money, hourly, rows, scoringVersion };
}
function heuresValides(declared) {
    return typeof declared === "number" && Number.isFinite(declared) && declared > 0 ? declared : 0;
}
/**
 * Chiffre le coût du temps sur la liste générique.
 *
 * C'est le chemin de repli : il ne sert qu'au visiteur qui entre directement
 * par le volet 2, sans être passé par le volet 1. Quand les tâches réelles
 * existent, `computeTimeCostFromTasks` leur est préféré — chiffrer « saisie et
 * recopie de données » à quelqu'un qui a déclaré « rapprochement de pièces »
 * revient à lui demander deux fois la même chose et à répondre à côté.
 *
 * @param heuresParTache heures déclarées par semaine, dans l'ordre de TIME_COST_TASKS
 * @param remunerationMensuelle rémunération mensuelle nette en F CFA
 */
export function computeTimeCost(heuresParTache, remunerationMensuelle) {
    const rows = TIME_COST_TASKS.map((task, i) => {
        const heures = heuresValides(heuresParTache[i]);
        return { ...task, heures, recuperables: heures * task.auto * WEEKS_PER_MONTH };
    });
    return chiffrer(rows, remunerationMensuelle);
}
/**
 * Chiffre le coût du temps sur les tâches réelles de l'utilisateur.
 *
 * Même formule, même version de barème : seule la provenance des lignes change.
 * Le montant annoncé porte alors sur ce que la personne a effectivement déclaré,
 * avec ses mots, ce qui le rend défendable devant elle.
 */
export function computeTimeCostFromTasks(taches, remunerationMensuelle) {
    const rows = taches.map((tache) => {
        const heures = heuresValides(tache.heures);
        const auto = AUTO_PAR_NIVEAU[tache.niveau];
        return { name: tache.texte, auto, heures, recuperables: heures * auto * WEEKS_PER_MONTH };
    });
    return chiffrer(rows, remunerationMensuelle);
}
